import { Component, ComponentType, ErrorInfo, ReactNode, Suspense, useEffect, useState } from "react";
import { Button } from "../Button";
import { Modal } from "../Modal";
import { ErrorState, LoadingSkeleton } from "../States";
import { useNextT } from "../../i18n";

/**
 * Why a part is not shown: `offline` its code did not arrive (Try again); `updated` it did not arrive again after Try
 * again, most likely a new release replaced it (only a page reload helps); `crashed` the part itself broke while drawing.
 */
export type PartFailure = { kind: "offline" | "updated" | "crashed"; retry: () => void };

/** The browser's own error for a script or style file of the app that did not arrive. */
export const isChunkError = (e: unknown): boolean => {
  const err = e as { name?: string; message?: string } | null;
  return err?.name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed/i.test(err?.message ?? "");
};

type CatchProps = { failed: (f: PartFailure) => ReactNode; onRetry: () => void; children: ReactNode };
type CatchState = { failed: boolean; chunk: boolean; retried: boolean };

/** Catches a part that could not be shown and tells which of the three it was. Every caught error is logged. */
class Catch extends Component<CatchProps, CatchState> {
  state: CatchState = { failed: false, chunk: false, retried: false };
  static getDerivedStateFromError(error: unknown) { return { failed: true, chunk: isChunkError(error) }; }
  componentDidCatch(error: unknown, info: ErrorInfo) { console.error(error, info.componentStack); }
  retry = () => { this.props.onRetry(); this.setState({ failed: false, retried: true }); };
  render() {
    const { failed, chunk, retried } = this.state;
    if (!failed) return this.props.children;
    const kind = !chunk ? "crashed" : retried ? "updated" : "offline";
    return this.props.failed({ kind, retry: this.retry });
  }
}

type HostProps = {
  part: ComponentType<object>;
  props: object;
  loading: ReactNode;
  failed: (f: PartFailure) => ReactNode;
  onRetry: () => void;
};

/** The part, its loading state while its code is on its way, and its failure if it could not be shown. */
export const PartHost = ({ part: Part, props, loading, failed, onRetry }: HostProps) => (
  <Catch failed={failed} onRetry={onRetry}>
    <Suspense fallback={loading}><Part {...props} /></Suspense>
  </Catch>
);

/** Nothing for the first `ms`, then the children: a part that arrives quickly never flashes its loading state. */
export const Delayed = ({ ms, children }: { ms: number; children: ReactNode }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(timer);
  }, [ms]);
  return shown ? <>{children}</> : null;
};

const reloadPage = () => window.location.reload();

type DialogBits = { open: boolean; onClose: () => void };

/** A quiet dialog while a dialog's code is on its way, shown only if it takes longer than a moment. */
export const DialogLoading = ({ open, onClose }: DialogBits) => {
  const { t } = useNextT();
  return <Delayed ms={250}><Modal open={open} title={t("ui.loading")} onClose={onClose}><LoadingSkeleton rows={4} /></Modal></Delayed>;
};

const failureWords = { offline: "ui.part_failed", updated: "ui.part_reload", crashed: "ui.part_crashed" } as const;

/** A dialog that could not be shown: plain words and the one way out (Try again, Reload the page, or Close). */
export const DialogFailed = ({ open, onClose, failure }: DialogBits & { failure: PartFailure }) => {
  const { t } = useNextT();
  const action = failure.kind === "offline" ? <Button variant="primary" onClick={failure.retry}>{t("ui.retry")}</Button>
    : failure.kind === "updated" ? <Button variant="primary" onClick={reloadPage}>{t("ui.reload_page")}</Button>
      : <Button variant="primary" onClick={onClose}>{t("ui.close")}</Button>;
  return (
    <Modal open={open} title={t("ui.error_title")} onClose={onClose} actions={action}>
      <p role="alert">{t(failureWords[failure.kind])}</p>
    </Modal>
  );
};

/** A panel's part that could not be shown: the kit's error state with the one way out (none for a crash). */
export const BlockFailed = ({ failure }: { failure: PartFailure }) => {
  const { t } = useNextT();
  const body = t(failureWords[failure.kind]);
  if (failure.kind === "offline") return <ErrorState body={body} onRetry={failure.retry} />;
  if (failure.kind === "updated") return <ErrorState body={body} onRetry={reloadPage} retryLabel={t("ui.reload_page")} />;
  return <ErrorState body={body} />;
};

export const BlockLoading = ({ rows }: { rows: number }) => <LoadingSkeleton rows={rows} />;
